<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import draggable from 'vuedraggable';
import TasksAPI from 'dashboard/api/tasks';
import TaskModal from 'dashboard/modules/tasks/components/TaskModal.vue';
import TaskForm from 'dashboard/modules/tasks/components/TaskForm.vue';
import TaskListItem from 'dashboard/modules/tasks/components/TaskListItem.vue';

const props = defineProps({
  selectedContact: { type: Object, default: null },
});

const { t } = useI18n();
const { updateTask } = useTaskActions();

const tasks = ref([]);
const localTasks = ref([]);
const dragging = ref(false);
const isLoading = ref(false);
const selectedTaskId = ref(null);
const showNewTaskForm = ref(false);

async function fetchTasks() {
  if (!props.selectedContact?.id) return;
  isLoading.value = true;
  try {
    const { data } = await TasksAPI.list({
      contact_id: props.selectedContact.id,
    });
    tasks.value = data.data ?? [];
    if (!dragging.value) localTasks.value = [...tasks.value];
  } catch {
    tasks.value = [];
    localTasks.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function handleComplete(task) {
  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
  await updateTask({
    taskId: task.id,
    status: newStatus,
    completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
  });
  await fetchTasks();
}

function handleOpen(task) {
  selectedTaskId.value = task.id;
}

function onTaskCreated() {
  showNewTaskForm.value = false;
  fetchTasks();
}

async function onDragEnd() {
  dragging.value = false;
  await Promise.all(
    localTasks.value.map((task, index) =>
      TasksAPI.update(task.id, { position: index })
    )
  );
}

onMounted(fetchTasks);
watch(() => props.selectedContact?.id, fetchTasks);
</script>

<template>
  <div class="flex flex-col gap-1 p-4">
    <!-- Botão adicionar tarefa -->
    <div class="flex justify-between items-center mb-2">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ $t('TASKS.HEADER') }}
      </h3>
      <div class="relative">
        <button
          class="text-xs flex items-center gap-1 text-n-brand hover:brightness-110 bg-n-alpha-1 px-2 py-1 rounded"
          @click="showNewTaskForm = !showNewTaskForm"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('TASKS.CONTACT_SECTION.ADD') }}
        </button>
        <div
          v-if="showNewTaskForm"
          class="absolute right-0 top-8 z-50 shadow-xl border border-n-weak rounded-xl"
        >
          <TaskForm
            :prefill="{ contact_id: selectedContact?.id }"
            @created="onTaskCreated"
            @cancel="showNewTaskForm = false"
          />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-xs text-n-slate-10 py-4 text-center">
      {{ t('TASKS.LOADING') }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="localTasks.length === 0"
      class="flex flex-col items-center justify-center py-8 gap-2 text-n-slate-9"
    >
      <span class="i-lucide-check-square size-6" />
      <span class="text-xs">{{ t('TASKS.CONTACT_SECTION.EMPTY') }}</span>
    </div>

    <!-- Lista de tarefas — igual à view de tarefas -->
    <draggable
      v-else
      v-model="localTasks"
      item-key="id"
      handle=".task-drag-item"
      ghost-class="opacity-40"
      class="border border-n-weak rounded-lg bg-n-surface-1 overflow-hidden"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <template #item="{ element: task }">
        <TaskListItem
          :task="task"
          stacked
          hide-completed-info
          @open="handleOpen"
          @complete="handleComplete"
          @updated="fetchTasks"
        />
      </template>
    </draggable>

    <!-- Modal de edição da tarefa -->
    <TaskModal
      v-if="selectedTaskId"
      :task-id="selectedTaskId"
      @close="selectedTaskId = null"
      @updated="fetchTasks"
      @deleted="
        selectedTaskId = null;
        fetchTasks();
      "
    />
  </div>
</template>
